/** Little-endian growable writer and bounds-checked reader over DataView. */
export class Writer {
  private buf: ArrayBuffer;
  private view: DataView;
  private pos = 0;

  constructor(initial = 256) {
    this.buf = new ArrayBuffer(initial);
    this.view = new DataView(this.buf);
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.byteLength) return;
    let size = this.buf.byteLength * 2;
    while (size < this.pos + n) size *= 2;
    const next = new ArrayBuffer(size);
    new Uint8Array(next).set(new Uint8Array(this.buf, 0, this.pos));
    this.buf = next;
    this.view = new DataView(next);
  }

  u8(v: number): void {
    this.ensure(1);
    this.view.setUint8(this.pos, clamp(v, 0, 255));
    this.pos += 1;
  }
  i8(v: number): void {
    this.ensure(1);
    this.view.setInt8(this.pos, clamp(v, -128, 127));
    this.pos += 1;
  }
  u16(v: number): void {
    this.ensure(2);
    this.view.setUint16(this.pos, clamp(v, 0, 65535), true);
    this.pos += 2;
  }
  i16(v: number): void {
    this.ensure(2);
    this.view.setInt16(this.pos, clamp(v, -32768, 32767), true);
    this.pos += 2;
  }
  u32(v: number): void {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.pos += 4;
  }
  f32(v: number): void {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
  }

  bytes(): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(new ArrayBuffer(this.pos));
    out.set(new Uint8Array(this.buf, 0, this.pos));
    return out;
  }
}

export class Reader {
  private readonly data: Uint8Array;
  private readonly view: DataView;
  private pos = 0;

  constructor(data: Uint8Array) {
    this.data = data;
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  get remaining(): number {
    return this.data.byteLength - this.pos;
  }

  private need(n: number): void {
    if (this.pos + n > this.data.byteLength) throw new RangeError("message truncated");
  }

  u8(): number {
    this.need(1);
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }
  i8(): number {
    this.need(1);
    const v = this.view.getInt8(this.pos);
    this.pos += 1;
    return v;
  }
  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }
  i16(): number {
    this.need(2);
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }
  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f32(): number {
    this.need(4);
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }
}

function clamp(v: number, min: number, max: number): number {
  const r = Math.round(v);
  return r < min ? min : r > max ? max : r;
}
